import { useState } from 'react'
import {
    Avatar,
    Badge,
    Box,
    Flex,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    IconButton,
    SimpleGrid,
    Stack,
    Text,
    useDisclosure
} from '@chakra-ui/react'
import { Button, Collapse, Select } from '@chakra-ui/react'
import { FiChevronDown, FiChevronUp, FiEdit2, FiLogIn, FiLogOut } from 'react-icons/fi'
import { ChildRecord } from '@/shared/api/rest/types'
import { AlternativeContactRecord } from '@/shared/api/rest/services/alternative-contacts.service'
import { useChildCheckinHistory } from '@/modules/teacher/hooks/useChildCheckinHistory'
import CheckinHistoryList from '@/modules/teacher/components/CheckinHistoryList'
import ChildQrCode from '@/modules/teacher/components/ChildQrCode'
import { useManualCheckInOut } from '@/modules/teacher/hooks/useManualCheckInOut'
import PermissionGate from '@/shared/components/PermissionGate'
import { UserTypes } from '@/shared/types/auth'
import { useBranches } from '@/shared/api/hooks/useBranches'
import { useMyTeacherBranchId } from '@/shared/api/hooks/useMyTeacherBranchId'
import { SERVICE_SESSION } from '@/modules/signup-page/types'
import { getChildName } from '../types'
import EditChildModal from './EditChildModal'

// Just enough to see the last couple of services without crowding the card.
const HISTORY_LIMIT = 3
const HISTORY_STALE_MS = 60_000

const SESSION_OPTIONS = [
    { value: SERVICE_SESSION.MORNING, label: 'Morning' },
    { value: SERVICE_SESSION.EVENING, label: 'Evening' }
]

type Props = { child: ChildRecord; guardians: AlternativeContactRecord[] }

const DetailItem = ({ label, value }: { label: string; value?: string | null }) => (
    <Box minW={0}>
        <Text
            fontSize="xs"
            textTransform="uppercase"
            color="gray.500"
            letterSpacing="wide"
        >
            {label}
        </Text>
        <Text fontSize="sm" noOfLines={1}>
            {value && value.trim() ? value : '—'}
        </Text>
    </Box>
)

const guardianName = (guardian: AlternativeContactRecord) =>
    [guardian.firstName, guardian.middleName, guardian.lastName]
        .filter(Boolean)
        .join(' ')
        .trim()

const guardianAddress = (guardian: AlternativeContactRecord) =>
    [guardian.address, guardian.city, guardian.state, guardian.country]
        .filter((value) => value && value.trim())
        .join(', ')

function GuardianRow({ guardian }: { guardian: AlternativeContactRecord }) {
    const address = guardianAddress(guardian)
    return (
        <Flex gap={3} align="center">
            <Avatar
                size="sm"
                name={guardianName(guardian)}
                src={guardian.picture || undefined}
                flexShrink={0}
            />
            <Box minW={0}>
                <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                    {guardianName(guardian)}
                    {guardian.title ? ` · ${guardian.title}` : ''}
                </Text>
                <Text fontSize="xs" color="gray.500" noOfLines={1}>
                    {guardian.phoneNumber || '—'}
                    {address ? ` · ${address}` : ''}
                </Text>
            </Box>
        </Flex>
    )
}

/** Full-width row for a child: profile picture, details, QR code, and guardians. */
export default function RegistryChildCard({ child, guardians }: Props) {
    const { isOpen, onOpen, onClose } = useDisclosure()
    // Collapsed by default so a parent with several children stays scannable.
    // The query still runs regardless — the Checkout Code above is derived from
    // it, so it must not depend on the section being expanded.
    const { isOpen: historyOpen, onToggle: toggleHistory } = useDisclosure()
    const { isOpen: editOpen, onOpen: openEdit, onClose: closeEdit } = useDisclosure()
    const allergies = child.allergies?.length ? child.allergies.join(', ') : null
    // `GET /checkins` has no childId filter, but it does accept qrCode — and the
    // child record carries qrCodeData, so that is how a single child's history
    // is read here.
    const { checkIn, checkOut, pending } = useManualCheckInOut()
    const [pickedSession, setPickedSession] = useState<string | null>(null)
    const [pickedBranch, setPickedBranch] = useState<string | null>(null)
    const { branches } = useBranches()
    const { branchId: myBranchId } = useMyTeacherBranchId()
    const {
        history,
        openCheckin,
        loading: historyLoading
    } = useChildCheckinHistory(child.qrCodeData, HISTORY_LIMIT, {
        staleTime: HISTORY_STALE_MS
    })

    // Checkout validates against the OPEN check-in, so that record's code is the
    // only one that will work. `children.lastCheckinCode` is a denormalised copy
    // that can lag behind, which is how a teacher ends up typing a code the
    // backend rejects. Fall back to it only when there is no history to read.
    const activeCode = openCheckin ? openCheckin.checkinCode ?? null : null
    const latestCode = history[0]?.checkinCode ?? null
    const checkoutCode = child.isCheckedIn
        ? activeCode ?? child.lastCheckinCode ?? null
        : latestCode ?? child.lastCheckinCode ?? null
    // An open check-in with no stored code can never be checked out by code —
    // surface that instead of showing a blank field.
    const codeMissingOnOpenCheckin = !!openCheckin && !openCheckin.checkinCode
    // Checking out defaults to the session the child was checked into, so the
    // record stays in the right bucket; checking in has nothing to inherit and
    // must be chosen. Derived rather than seeded via an effect.
    const sessionValue = pickedSession ?? openCheckin?.session ?? null
    // Defaults to the teacher's own branch (their teacher record), falling back
    // to the branch picker. Derived, so no effect is needed to seed the select.
    const branchValue = pickedBranch ?? myBranchId ?? ''

    return (
        // A heavier outer edge than the gray.100 hairlines used inside the card,
        // so each child reads as its own block in a long list of siblings.
        <Box borderWidth="2px" borderColor="gray.200" borderRadius="md" p={4} bg="white">
            <Flex
                gap={4}
                align={{ base: 'stretch', md: 'center' }}
                direction={{ base: 'column', md: 'row' }}
            >
                <Avatar
                    size="lg"
                    name={getChildName(child)}
                    src={child.picture ?? undefined}
                    flexShrink={0}
                />

                <Box flex="1" minW={0}>
                    <Flex justify="space-between" align="start" gap={2} flexWrap="wrap">
                        <Box minW={0}>
                            {/* The pencil sits against the NAME, not the status
                                badge — beside the badge it reads as a way to
                                edit the check-in, which is not what it does. */}
                            <Flex align="center" gap={1} minW={0}>
                                <Text fontWeight="semibold" noOfLines={1}>
                                    {getChildName(child)}
                                </Text>
                                <PermissionGate
                                    allowedTypes={[
                                        UserTypes.IS_ADMIN_TEACHER,
                                        UserTypes.IS_GLOBAL_ADMIN
                                    ]}
                                >
                                    <IconButton
                                        size="xs"
                                        variant="ghost"
                                        colorScheme="gray"
                                        icon={<FiEdit2 />}
                                        onClick={openEdit}
                                        aria-label={`Edit ${getChildName(child)}`}
                                        title="Edit child details"
                                        flexShrink={0}
                                    />
                                </PermissionGate>
                            </Flex>
                            <Text fontSize="xs" color="gray.500">
                                {child.ageGroup || 'No age group'}
                                {typeof child.age === 'number'
                                    ? ` · ${child.age} yrs`
                                    : ''}
                            </Text>
                        </Box>
                        <Badge
                            colorScheme={child.isCheckedIn ? 'green' : 'gray'}
                            flexShrink={0}
                        >
                            {child.isCheckedIn ? 'Checked in' : 'Checked out'}
                        </Badge>
                    </Flex>

                    <SimpleGrid
                        columns={{ base: 2, sm: 3, lg: 4 }}
                        spacingX={4}
                        spacingY={2}
                        mt={3}
                    >
                        <DetailItem label="Gender" value={child.gender} />
                        <DetailItem label="Date of Birth" value={child.dateOfBirth} />
                        <DetailItem label="Blood Group" value={child.bloodGroup} />
                        <DetailItem label="Genotype" value={child.genotype} />
                        <DetailItem label="Allergies" value={allergies} />
                        {/* Each check-in issues its own code, so this sits with
                            the other per-child details at the same scale. */}
                        <DetailItem
                            label="Checkout Code"
                            value={codeMissingOnOpenCheckin ? 'Not set' : checkoutCode}
                        />
                    </SimpleGrid>

                    {codeMissingOnOpenCheckin ? (
                        <Text fontSize="xs" color="red.600" mt={2}>
                            This check-in has no code on record — use &ldquo;check out
                            without QR&rdquo; below to release the child.
                        </Text>
                    ) : null}
                </Box>

                <Box textAlign="center" flexShrink={0} alignSelf="center">
                    <ChildQrCode
                        qrCodeData={child.qrCodeData}
                        label={`QR code for ${getChildName(child)}`}
                        onClick={onOpen}
                    />
                    <Text fontSize="xs" color="gray.400" mt={1}>
                        QR Code
                    </Text>
                </Box>
            </Flex>

            <Box mt={3} pt={3} borderTopWidth="1px" borderColor="gray.100">
                <Flex
                    as="button"
                    type="button"
                    align="center"
                    gap={2}
                    onClick={toggleHistory}
                    aria-expanded={historyOpen}
                    w="full"
                    textAlign="left"
                >
                    <Text
                        fontSize="xs"
                        textTransform="uppercase"
                        color="gray.500"
                        letterSpacing="wide"
                    >
                        Recent check-in / check-out
                        {history.length > 0 ? ` (${history.length})` : ''}
                    </Text>
                    <Box color="gray.500" fontSize="12px" display="inline-flex">
                        {historyOpen ? <FiChevronUp /> : <FiChevronDown />}
                    </Box>
                </Flex>

                <Collapse in={historyOpen} animateOpacity>
                    <Box pt={2}>
                        <CheckinHistoryList
                            history={history}
                            loading={historyLoading}
                            compact
                            showTitle={false}
                            emptyMessage="No check-in history recorded."
                        />
                    </Box>
                </Collapse>

                {/* Override path for when the QR or the check-in code can't be
                    used. Gated to admin teachers and global admins: it bypasses
                    check-in code validation entirely. */}
                <PermissionGate
                    allowedTypes={[UserTypes.IS_ADMIN_TEACHER, UserTypes.IS_GLOBAL_ADMIN]}
                >
                    {/* Session and branch share one row so the override stays
                        compact; branch only applies to a new check-in. */}
                    <Flex gap={4} mt={3} flexWrap="wrap" align="flex-start">
                        <Box>
                            <Text
                                fontSize="xs"
                                textTransform="uppercase"
                                color="gray.500"
                                letterSpacing="wide"
                            >
                                Service session
                            </Text>
                            <Flex gap={1} mt={1} flexWrap="wrap">
                                {SESSION_OPTIONS.map(({ value, label }) => {
                                    const isSelected = sessionValue === value
                                    return (
                                        <Box
                                            as="button"
                                            type="button"
                                            key={value}
                                            onClick={() => setPickedSession(value)}
                                            px={2}
                                            py={1}
                                            borderRadius="full"
                                            borderWidth={isSelected ? '2px' : '1px'}
                                            fontSize="xs"
                                            fontWeight={
                                                isSelected ? 'semibold' : 'normal'
                                            }
                                            borderColor={
                                                isSelected ? 'brand.600' : 'gray.300'
                                            }
                                            bg={isSelected ? 'brand.50' : 'white'}
                                            color={isSelected ? 'brand.400' : 'gray.600'}
                                            _hover={{
                                                borderColor: isSelected
                                                    ? 'brand.600'
                                                    : 'gray.400'
                                            }}
                                            aria-pressed={isSelected}
                                        >
                                            {label}
                                        </Box>
                                    )
                                })}
                            </Flex>
                        </Box>

                        {child.isCheckedIn ? null : (
                            <Box>
                                <Text
                                    fontSize="xs"
                                    textTransform="uppercase"
                                    color="gray.500"
                                    letterSpacing="wide"
                                >
                                    Branch
                                </Text>
                                <Select
                                    size="xs"
                                    mt={1}
                                    minW="200px"
                                    maxW="240px"
                                    borderRadius="md"
                                    placeholder="Select branch"
                                    aria-label="Branch for this check-in"
                                    value={branchValue}
                                    onChange={(event) =>
                                        setPickedBranch(event.target.value)
                                    }
                                >
                                    {branches.map((branch) => (
                                        <option key={branch.value} value={branch.value}>
                                            {branch.label}
                                        </option>
                                    ))}
                                </Select>
                            </Box>
                        )}
                    </Flex>

                    <Flex gap={2} mt={2} flexWrap="wrap">
                        {child.isCheckedIn ? (
                            <Button
                                size="xs"
                                colorScheme="red"
                                variant="outline"
                                leftIcon={<FiLogOut />}
                                isDisabled={pending || !sessionValue}
                                onClick={() =>
                                    checkOut({
                                        childId: child._id,
                                        session: sessionValue ?? undefined
                                    }).catch(() => undefined)
                                }
                            >
                                Check out without QR
                            </Button>
                        ) : (
                            <Button
                                size="xs"
                                colorScheme="green"
                                variant="outline"
                                leftIcon={<FiLogIn />}
                                isDisabled={pending || !sessionValue || !branchValue}
                                onClick={() =>
                                    checkIn({
                                        childId: child._id,
                                        branch: branchValue || undefined,
                                        session: sessionValue ?? undefined
                                    }).catch(() => undefined)
                                }
                            >
                                Check in without QR
                            </Button>
                        )}
                        <Text fontSize="xs" color="gray.500" alignSelf="center">
                            {!sessionValue
                                ? 'Pick a session to continue.'
                                : !child.isCheckedIn && !branchValue
                                  ? 'Pick a branch to continue.'
                                  : 'Skips the check-in code. Recorded against you.'}
                        </Text>
                    </Flex>
                </PermissionGate>
            </Box>

            {guardians.length > 0 ? (
                <Box mt={3} pt={3} borderTopWidth="1px" borderColor="gray.100">
                    <Text
                        fontSize="xs"
                        textTransform="uppercase"
                        color="gray.500"
                        letterSpacing="wide"
                        mb={2}
                    >
                        {guardians.length === 1 ? 'Guardian' : 'Guardians'}
                    </Text>
                    <Stack spacing={2}>
                        {guardians.map((guardian) => (
                            <GuardianRow key={guardian._id} guardian={guardian} />
                        ))}
                    </Stack>
                </Box>
            ) : null}

            <EditChildModal child={child} isOpen={editOpen} onClose={closeEdit} />

            {child.qrCodeData ? (
                <Modal isOpen={isOpen} onClose={onClose} isCentered size="sm">
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>{getChildName(child)}</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody pb={6}>
                            <Flex direction="column" align="center" gap={3}>
                                <ChildQrCode
                                    qrCodeData={child.qrCodeData}
                                    label={`QR code for ${getChildName(child)}`}
                                    size="280px"
                                />
                                <Text fontSize="sm" color="gray.500">
                                    {child.ageGroup || 'No age group'}
                                    {typeof child.age === 'number'
                                        ? ` · ${child.age} yrs`
                                        : ''}
                                </Text>
                            </Flex>
                        </ModalBody>
                    </ModalContent>
                </Modal>
            ) : null}
        </Box>
    )
}
